import { gatewayApi as api } from './gatewayApi';

// User Types
export interface User {
  Id?: string;
  FirstName: string;
  LastName: string;
  Email: string;
  EmployeeNumber?: string;
  RoleId: string;
  DepartmentId: string;
  Active: boolean;
  IsSupervisor: boolean;
  UserImage?: string;
  Role?: Role;
  Department?: Department;
  CreatedDate?: string;
  UpdatedDate?: string;
}

export interface CreateUserRequest {
  FirstName: string;
  LastName: string;
  Email: string;
  EmployeeNumber?: string;
  RoleId: string;
  DepartmentId: string;
  Active: boolean;
  IsSupervisor: boolean;
  UserImage?: string;
}

export interface UpdateUserRequest extends Partial<CreateUserRequest> {
  Id: string;
}

export interface PasswordResetRequest {
  id: string;
  data: Pick<User, 'Id' | 'FirstName' | 'LastName' | 'Email'>;
}

// Role Types
export interface Role {
  Id?: string;
  Name: string;
  Description?: string;
  CanApproveParts: boolean;
  CanStampOperations: boolean;
  CanApproveMaterials: boolean;
  CanModifyPOQTY: boolean;
  CanDeleteNCR: boolean;
  Active: boolean;
  Permissions?: Permission[];
  CreatedDate?: string;
  UpdatedDate?: string;
}

export interface CreateRoleRequest {
  Name: string;
  Description?: string;
  CanApproveParts: boolean;
  CanStampOperations: boolean;
  CanApproveMaterials: boolean;
  CanModifyPOQTY: boolean;
  CanDeleteNCR: boolean;
  Active: boolean;
  Permissions?: string[]; // Permission IDs
}

export interface UpdateRoleRequest extends Partial<CreateRoleRequest> {
  Id: string;
}

// Permission Types
export interface Permission {
  Id: string;
  Name: string;
  Description?: string;
  Module?: string;
  Action?: string;
  Active: boolean;
}

// Department Types
export interface Department {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

// Subscription/Billing Types
export interface BillingInfo {
  currentBilling: number;
  amount: number;
  total: number;
}

// Response Types
export interface UsersResponse {
  data: User[];
  total: number;
  page: number;
  limit: number;
}

export interface RolesResponse {
  data: Role[];
  total: number;
  page: number;
  limit: number;
}

export interface UserStatistics {
  total: number;
  active: number;
  inactive: number;
  supervisors: number;
}

export interface RoleStatistics {
  total: number;
  active: number;
  inactive: number;
  withPermissions: number;
}

// Admin API Service
export const adminApi = {
  // User Management
  users: {
    // Get all users with pagination and filters
    getAll: (params?: { 
      page?: number; 
      limit?: number; 
      search?: string; 
      active?: boolean; 
      roleId?: string;
      departmentId?: string;
    }): Promise<UsersResponse> =>
      api.get<UsersResponse>('/user', params),

    // Get user by ID
    getById: (id: string): Promise<User> =>
      api.get<User>(`/user/${id}`),

    // Create new user
    create: (user: CreateUserRequest): Promise<User> =>
      api.post<User>('/user', user),

    // Update user
    update: (id: string, user: UpdateUserRequest): Promise<User> =>
      api.put<User>(`/user/${id}`, user),

    // Delete user
    delete: (id: string): Promise<void> =>
      api.delete<void>(`/user/${id}`),

    // Search users
    search: (filters: Record<string, any>): Promise<User[]> =>
      api.post<User[]>('/user/search', { filter: filters }),

    // Get user image
    getImage: (userId: string): Promise<string> =>
      api.get<string>(`/user/${userId}/image`),

    // Password reset
    passwordReset: (request: PasswordResetRequest): Promise<void> =>
      api.put<void>('/user/passwordreset', request),

    // Get user statistics
    getStatistics: (): Promise<UserStatistics> =>
      api.get<UserStatistics>('/user/statistics'),

    // Bulk operations
    bulkActivate: (userIds: string[]): Promise<void> =>
      api.post<void>('/user/bulk/activate', { ids: userIds }),

    bulkDeactivate: (userIds: string[]): Promise<void> =>
      api.post<void>('/user/bulk/deactivate', { ids: userIds }),

    // Export users to CSV
    export: (): Promise<Blob> =>
      api.getBlob('/user/export'),
  },

  // Role Management
  roles: {
    // Get all roles with pagination and filters
    getAll: (params?: { 
      page?: number; 
      limit?: number; 
      search?: string; 
      active?: boolean;
    }): Promise<RolesResponse> =>
      api.get<RolesResponse>('/role', params),

    // Get role by ID
    getById: (id: string): Promise<Role> =>
      api.get<Role>(`/role/${id}`),

    // Create new role
    create: (role: CreateRoleRequest): Promise<Role> =>
      api.post<Role>('/role', role),

    // Update role
    update: (id: string, role: UpdateRoleRequest): Promise<Role> =>
      api.put<Role>(`/role/${id}`, role),

    // Delete role
    delete: (id: string): Promise<void> =>
      api.delete<void>(`/role/${id}`),

    // Search roles
    search: (filters: Record<string, any>): Promise<Role[]> =>
      api.post<Role[]>('/role/search', { filter: filters }),

    // Get unassigned permissions for a role
    getUnassignedPermissions: (roleId: string): Promise<Permission[]> =>
      api.get<Permission[]>(`/role/${roleId}/unassigned`),

    // Get role statistics
    getStatistics: (): Promise<RoleStatistics> =>
      api.get<RoleStatistics>('/role/statistics'),

    // Clone role
    clone: (roleId: string, newName: string): Promise<Role> =>
      api.post<Role>(`/role/${roleId}/clone`, { name: newName }),

    // Export roles to CSV
    export: (): Promise<Blob> =>
      api.getBlob('/role/export'),
  },

  // Permission Management
  permissions: {
    // Get all permissions
    getAll: (): Promise<Permission[]> =>
      api.get<Permission[]>('/role/permission'),

    // Get permissions by module
    getByModule: (module: string): Promise<Permission[]> =>
      api.get<Permission[]>(`/role/permission/module/${module}`),
  },

  // Department Management
  departments: {
    // Get all departments
    getAll: (): Promise<Department[]> =>
      api.get<Department[]>('/department'),

    // Get active departments only
    getActive: (): Promise<Department[]> =>
      api.get<Department[]>('/department', { active: true }),
  },

  // Subscription Management
  subscription: {
    // Get subscription plan/billing info
    getPlan: (): Promise<BillingInfo> =>
      api.get<BillingInfo>('/subscription/plan'),

    // Cancel subscription
    cancel: (): Promise<void> =>
      api.post<void>('/subscription/cancel', {}),
  },

  // Validation helpers
  validation: {
    validateUser: (user: Partial<User>): string[] => {
      const errors: string[] = [];
      
      if (!user.FirstName?.trim()) {
        errors.push('First Name is required');
      }
      
      if (!user.LastName?.trim()) {
        errors.push('Last Name is required');
      }
      
      if (!user.Email?.trim()) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.Email)) {
        errors.push('Email must be valid');
      }
      
      if (!user.RoleId) {
        errors.push('Role is required');
      }
      
      if (!user.DepartmentId) {
        errors.push('Department is required');
      }
      
      return errors;
    },

    validateRole: (role: Partial<Role>): string[] => {
      const errors: string[] = [];
      
      if (!role.Name?.trim()) {
        errors.push('Role Name is required');
      }
      
      return errors;
    },
  },
}; 